import { getSortedFloat64 } from "./getSortedFloat64.js";
import { boxPlotFromSorted } from "./xBoxPlot.js";
import { xCheck } from "./xCheck.js";
/**
 * Calculating the box plot of the array with outliers
 * Values are outliers if they are below Q1 - 1.5 * IQR or above Q3 + 1.5 * IQR
 * @param array - data
 * @returns - q1, median, q3, min, max, outliers
 */
export function xBoxPlotWithOutliers(array) {
    xCheck(array);
    const sorted = getSortedFloat64(array);
    return boxPlotWithOutliersFromSorted(sorted);
}
/**
 * Calculating the box plot with outliers of an already-sorted array, without copying
 * or re-sorting. Because the array is sorted, the outliers are the low prefix and the
 * high suffix, so they are reported in ascending order.
 * Internal helper: not re-exported from `x/index.ts`, so it stays out of the public API.
 * @param sorted - sorted data.
 * @returns - q1, median, q3, min, max, outliers
 */
export function boxPlotWithOutliersFromSorted(sorted) {
    return boxPlotWithOutliersAndBounds(sorted).boxPlot;
}
/**
 * Same computation as {@link boxPlotWithOutliersFromSorted}, but also returns the
 * non-outlier index range so callers that need the filtered values (mean, sd, …) can
 * reuse the bounds instead of scanning the array a second time.
 * Internal helper: not re-exported from `x/index.ts`, so it stays out of the public API.
 * @param sorted - sorted data.
 * @returns - the box plot and the non-outlier index range.
 */
export function boxPlotWithOutliersAndBounds(sorted) {
    const boxPlot = boxPlotFromSorted(sorted);
    if (boxPlot.max - boxPlot.min <= Number.EPSILON) {
        return {
            boxPlot: {
                ...boxPlot,
                lowerWhisker: boxPlot.min,
                upperWhisker: boxPlot.max,
                minWhisker: boxPlot.min,
                maxWhisker: boxPlot.max,
                iqr: 0,
                outliers: [],
            },
            bounds: { lowIndex: 0, highIndex: sorted.length },
        };
    }
    const iqr = boxPlot.q3 - boxPlot.q1;
    const lowerWhisker = boxPlot.q1 - 1.5 * iqr;
    const upperWhisker = boxPlot.q3 + 1.5 * iqr;
    const bounds = getWhiskerBounds(sorted, lowerWhisker, upperWhisker);
    const { lowIndex, highIndex } = bounds;
    const outliers = [];
    for (let i = 0; i < lowIndex; i++) {
        outliers.push(sorted[i]);
    }
    for (let i = highIndex; i < sorted.length; i++) {
        outliers.push(sorted[i]);
    }
    return {
        boxPlot: {
            ...boxPlot,
            lowerWhisker,
            upperWhisker,
            minWhisker: sorted[lowIndex],
            maxWhisker: sorted[highIndex - 1],
            iqr,
            outliers,
        },
        bounds,
    };
}
/**
 * Locate the contiguous range `[lowIndex, highIndex)` of non-outliers in a sorted array.
 * Because the array is sorted, outliers are exactly the prefix below `lowerWhisker` and
 * the suffix above `upperWhisker`, so the scan only touches the outliers themselves.
 * Internal helper: not re-exported from `x/index.ts`, so it stays out of the public API.
 * @param sorted - sorted data.
 * @param lowerWhisker - values strictly below are low outliers.
 * @param upperWhisker - values strictly above are high outliers.
 * @returns the non-outlier index range.
 */
export function getWhiskerBounds(sorted, lowerWhisker, upperWhisker) {
    let lowIndex = 0;
    while (lowIndex < sorted.length && sorted[lowIndex] < lowerWhisker) {
        lowIndex++;
    }
    let highIndex = sorted.length;
    while (highIndex > lowIndex && sorted[highIndex - 1] > upperWhisker) {
        highIndex--;
    }
    return { lowIndex, highIndex };
}
//# sourceMappingURL=xBoxPlotWithOutliers.js.map